import {
  calculateBillingPeriodEnd,
  calculateInvoiceTotals,
  calculateLineItemTax,
  formatLineItemDescription,
  parseDateInputValue,
  type BillingFrequency,
} from "./billingPeriodUtils";
import { parseCcEmails } from "./billingSaveUtils";
import type {
  InvoiceAddressBlock,
  InvoicePreviewLineItem,
  SaveInvoiceItemPayload,
  SaveInvoicePayload,
} from "./types";

function formatDateForApi(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const INVOICE_DETAIL_LINE_SEP = "\n";

export function formatAddressBlockAsDetails(
  address: InvoiceAddressBlock
): string {
  const lines = [address.name, ...address.lines].filter(part => part?.trim());

  if (address.gstin?.trim()) {
    lines.push(`GST NO: ${address.gstin.trim()}`);
  }

  return lines.join(INVOICE_DETAIL_LINE_SEP);
}

function getInvoiceBillingPeriodRange(lineItems: InvoicePreviewLineItem[]): {
  from: string;
  to: string | null;
} {
  const startDates = lineItems
    .map(item => parseDateInputValue(item.billingStartDate))
    .filter((date): date is Date => date !== null);

  if (startDates.length === 0) {
    return { from: "", to: null };
  }

  const fromDate = new Date(
    Math.min(...startDates.map(date => date.getTime()))
  );

  const recurringEndDates = lineItems
    .map(item => {
      if (!item.frequency || item.frequency === "onetime") {
        return null;
      }
      const startDate = parseDateInputValue(item.billingStartDate);
      if (!startDate) {
        return null;
      }
      return calculateBillingPeriodEnd(
        startDate,
        item.frequency as BillingFrequency
      );
    })
    .filter((date): date is Date => date !== null);

  // All one-time (or no recurring ends): no billing_period_to
  if (recurringEndDates.length === 0) {
    return {
      from: formatDateForApi(fromDate),
      to: null,
    };
  }

  const toDate = new Date(
    Math.max(...recurringEndDates.map(date => date.getTime()))
  );

  return {
    from: formatDateForApi(fromDate),
    to: formatDateForApi(toDate),
  };
}

function getLineItemBillingPeriodRange(item: InvoicePreviewLineItem): {
  from: string;
  to: string | null;
} {
  const startDate = parseDateInputValue(item.billingStartDate);
  if (!startDate) {
    return { from: "", to: null };
  }

  const from = formatDateForApi(startDate);

  // One Time: start date only — billing_period_to must be null
  if (!item.frequency || item.frequency === "onetime") {
    return { from, to: null };
  }

  const endDate = calculateBillingPeriodEnd(
    startDate,
    item.frequency as BillingFrequency
  );

  return {
    from,
    to: formatDateForApi(endDate),
  };
}

function buildSaveInvoiceItem(
  item: InvoicePreviewLineItem,
  gstType?: string
): SaveInvoiceItemPayload {
  const tax = calculateLineItemTax(
    item.amount,
    item.gstApplicable,
    item.gstRate,
    gstType
  );
  const billingPeriod = getLineItemBillingPeriodRange(item);

  return {
    description: formatLineItemDescription(item),
    hsn_sac: item.hsnSac,
    qty: item.qty,
    rate: item.rate,
    amount: item.amount,
    cgst: item.gstApplicable && tax.isInstate ? tax.halfGstRate : 0,
    sgst: item.gstApplicable && tax.isInstate ? tax.halfGstRate : 0,
    igst: item.gstApplicable && !tax.isInstate ? tax.fullGstRate : 0,
    cgst_amount: tax.cgst,
    sgst_amount: tax.sgst,
    igst_amount: tax.igst,
    total: item.amount + tax.taxTotal,
    billing_period_from: billingPeriod.from,
    billing_period_to: billingPeriod.to,
  };
}

export function buildSaveInvoicePayload(input: {
  invoiceTitle: string;
  invoiceNumber: string;
  invoiceDate: string;
  from: InvoiceAddressBlock;
  billTo: InvoiceAddressBlock;
  lineItems: InvoicePreviewLineItem[];
  notes: string;
  gracePeriodDays: string;
  projectId: string;
  clientName: string;
  clientBillingEmail: string;
  ccEmails: string;
  clientGstType?: string;
  billingConfigurationId: string;
}): SaveInvoicePayload {
  const totals = calculateInvoiceTotals(input.lineItems, input.clientGstType);
  const billingPeriod = getInvoiceBillingPeriodRange(input.lineItems);
  const trimmedNotes = input.notes.trim();
  const dueDays = Number(input.gracePeriodDays) || 0;

  return {
    invoice_title: input.invoiceTitle.trim(),
    invoice_number: input.invoiceNumber.trim(),
    invoice_date: input.invoiceDate,
    billing_period_from: billingPeriod.from,
    billing_period_to: billingPeriod.to,
    from_details: formatAddressBlockAsDetails(input.from),
    to_details: formatAddressBlockAsDetails(input.billTo),
    project: input.projectId,
    client_name: input.clientName,
    client_billing_email: input.clientBillingEmail.trim(),
    cc_emails: parseCcEmails(input.ccEmails),
    notes:
      trimmedNotes || (dueDays > 0 ? `Payment due within ${dueDays} days` : ""),
    billing_id: input.billingConfigurationId.trim(),
    items: input.lineItems.map(item =>
      buildSaveInvoiceItem(item, input.clientGstType)
    ),
    subtotal: totals.subtotal,
    tax_total: totals.taxTotal,
    grand_total: totals.total,
  };
}
